import * as React from 'react';

import { cva } from 'class-variance-authority';
import type { Key } from 'react-aria-components';
import {
  Autocomplete,
  Menu,
  MenuItem,
  composeRenderProps,
  useFilter,
} from 'react-aria-components';
import { useHotkeys } from 'react-hotkeys-hook';

import { cn } from '../../../lib/stylingUtils';
import { ComplexKeyboardShortcut } from '../../../types/keyboard';
import { TextField } from '../forms/TextField';

import { DialogContent, DialogOverlay } from './Dialog';

const commandBarVariants = cva([
  'group/dialog-overlay fixed inset-0 z-50 bg-transparent backdrop-blur-md',
]);

const commandBarContentVariants = cva([
  'w-full max-w-xl gap-0 border border-foreground/20 px-1 py-2 shadow-lg',
]);

const commandBarInputVariants = cva([
  'border-b border-none border-foreground/20 *:border-none *:!ring-transparent *:outline-none',
]);

const commandItemVariants = cva([
  'group box-border flex w-full cursor-default flex-col items-start gap-1 rounded-md px-3 py-2 text-foreground outline-none',
  /* Hovered */
  'rac-hover:bg-primary/20 rac-hover:text-foreground',
  /* Focused */
  'rac-focus:bg-primary rac-focus:text-space',
  /* Pressed */
  'rac-pressed:bg-primary/80 rac-pressed:text-space',
]);

export interface CommandBarAction {
  /**
   * Unique identifier for the action
   */
  id: string;

  /**
   * Display label for the action
   */
  label: string;

  /**
   * Optional icon to display alongside the label
   */
  icon?: React.ReactNode;

  /**
   * Optional group or category for the action
   */
  group?: string;

  /**
   * Keywords to help with filtering (not displayed)
   */
  keywords?: string[];

  /**
   * Optional subtitle or description
   */
  description?: string;
}

export interface CommandBarProps {
  /**
   * Actions to display in the command bar
   */
  actions: CommandBarAction[];

  /**
   * Function to execute when an action is selected
   */
  onAction: (actionId: string) => void;

  /**
   * Whether the command bar is open
   */
  isOpen: boolean;

  /**
   * Function to call when the command bar should close
   */
  onOpenChange?: (isOpen: boolean) => void;

  /**
   * Optional keyboard shortcut pattern (defaults to 'mod+k')
   * Using 'mod' will map to Cmd on Mac and Ctrl on Windows/Linux
   */
  keyboardShortcut?: ComplexKeyboardShortcut;

  /**
   * Placeholder text for the input
   */
  placeholder?: string;

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * CommandBar component
 *
 * A command palette-style interface that can be triggered with a keyboard shortcut.
 * Displays filterable actions that users can select.
 *
 * @example
 * ```tsx
 * const actions = [
 *   { id: 'new-file', label: 'New File', icon: <FileIcon /> },
 *   { id: 'new-folder', label: 'New Folder', icon: <FolderIcon /> },
 * ];
 *
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <CommandBar
 *   actions={actions}
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onAction={(id) => {
 *     console.log(`Action ${id} selected`);
 *     setIsOpen(false);
 *   }}
 * />
 * ```
 */
export const CommandBar = ({
  actions,
  onAction,
  isOpen,
  onOpenChange,
  keyboardShortcut,
  placeholder,
  className,
}: CommandBarProps) => {
  const [, setInputValue] = React.useState('');

  const { contains } = useFilter({ sensitivity: 'base' });

  useHotkeys(
    keyboardShortcut ?? 'Cmd+Shift+P',
    (event) => {
      console.log(event);
      event.preventDefault();

      if (!isOpen) {
        onOpenChange?.(true);
      }
    },
    { enabled: !!keyboardShortcut },
  );

  React.useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const handleSelect = React.useCallback(
    (key: Key) => {
      if (key === 'all') return;

      const selectedKey = actions.find((a) => a.id === key)?.id;
      if (selectedKey) onAction(selectedKey);
    },
    [actions, onAction],
  );

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={true}
      className={composeRenderProps(className, (className) =>
        cn('@CommandBar', commandBarVariants(), className),
      )}
    >
      <DialogContent
        position="fixed"
        className={cn(commandBarContentVariants(), className)}
        closeButton={false}
      >
        <div>
          <Autocomplete filter={contains}>
            <TextField
              autoFocus
              aria-label={placeholder}
              placeholder={placeholder}
              className={cn(commandBarInputVariants())}
            />
            <Menu
              items={actions}
              onAction={handleSelect}
              className="mt-2 max-h-44 overflow-auto p-1"
            >
              {(action: CommandBarAction) => <CommandItem action={action} />}
            </Menu>
          </Autocomplete>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};

const CommandItem = ({
  action,
  className,
  ...props
}: {
  action: CommandBarAction;
  className?: string;
} & React.PropsWithChildren) => (
  <MenuItem
    key={action.id}
    textValue={`${action.label} - ${action.description}`}
    {...props}
    className={cn('@CommandItem', commandItemVariants(), className)}
  >
    <div className="flex flex-row items-center gap-2 text-sm">
      {action.icon}
      {action.label}
    </div>
    {action.description && (
      <div className="ml-0.5 text-xs text-foreground/60 group-data-[focused]:text-space/80 group-data-[hovered]:text-space/80">
        {action.description}
      </div>
    )}
  </MenuItem>
);

export default CommandBar;
